import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import React, { useEffect, useState } from 'react'

interface ModalProps {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  title: string
  description?: string
  children: React.ReactNode
  className?: string
  overlayClassName?: string
  showClose?: boolean
  hideTitle?: boolean
}

export function Modal({
  open,
  defaultOpen,
  onOpenChange,
  title,
  description,
  children,
  className,
  overlayClassName,
  showClose = true,
  hideTitle = false,
}: ModalProps) {
  // Internal visibility state for AnimatePresence
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (open) setVisible(true)
  }, [open])

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setVisible(false)
      // Delay onOpenChange until after exit animation
      setTimeout(() => {
        onOpenChange?.(false)
      }, 200)
    } else {
      setVisible(true)
      onOpenChange?.(true)
    }
  }

  return (
    <Dialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        {(open || visible) && (
          <>
            <Dialog.Overlay
              forceMount
              className={clsx(
                'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity',
                overlayClassName,
              )}
            />
            <Dialog.Content
              forceMount
              className="fixed left-1/2 top-1/2 z-[60] w-full max-w-2xl md:max-w-3xl -translate-x-1/2 -translate-y-1/2"
            >
              <motion.div
                animate={visible ? { scale: 1, opacity: 1 } : { scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className={clsx(
                  'bg-white rounded-lg shadow-2xl p-8 focus:outline-none border border-brand-navy',
                  className,
                )}
              >
                {showClose && (
                  <Dialog.Close asChild>
                    <button
                      className="absolute right-4 top-4 text-brand-blue hover:text-brand-navy focus:outline-none border-none bg-transparent cursor-pointer p-2"
                      aria-label="Close"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        fill="none"
                        strokeWidth="1.5"
                      >
                        <line
                          x1="2"
                          y1="2"
                          x2="22"
                          y2="22"
                          stroke="currentColor"
                          strokeLinecap="round"
                        />
                        <line
                          x1="22"
                          y1="2"
                          x2="2"
                          y2="22"
                          stroke="currentColor"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </Dialog.Close>
                )}
                {hideTitle ? (
                  <VisuallyHidden asChild>
                    <Dialog.Title>{title}</Dialog.Title>
                  </VisuallyHidden>
                ) : (
                  <Dialog.Title className="text-3xl font-light leading-tight tracking-tight text-text-primary sm:text-5xl">
                    {title}
                  </Dialog.Title>
                )}
                {description ? (
                  <Dialog.Description className="mb-4 text-base text-brand-navy/80">
                    {description}
                  </Dialog.Description>
                ) : (
                  <VisuallyHidden asChild>
                    <Dialog.Description>{description}</Dialog.Description>
                  </VisuallyHidden>
                )}
                <div className="max-h-[80vh] overflow-y-auto pr-4 -mr-4">{children}</div>
              </motion.div>
            </Dialog.Content>
          </>
        )}
      </Dialog.Portal>
    </Dialog.Root>
  )
}
